import CsiFormData from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import def from "csi/def";
import { View } from "csi/def/View";
import message from "csi/message";
import { ControllerSaveFunction } from "csi/middleware/common";
import { GoToDialogHelper } from "csi/middleware/goToDialog/GoToDialogHelper";
import reflection from "csi/reflection";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, ICache, cnl } from "csi/typings";
import xml from "csi/xml";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable } from "mobx";
import { SyntheticEvent, createContext, useContext } from "react";
import ProductType from "src/Apparel/ProductType";
import { setColorAllocation } from "./ColorwayAllocationManageService";

export interface ColorProductSource extends ComponentItem<any> {
	/** Indicates if the color source is active */
	active: boolean;

	/** Indicates ColorProductSource cnl */
	cnl?: cnl;

	/** Indicates card status label */
	status: ColorSourceStatus;

	/** Indicates status color for card status label */
	statusColor?: string;
	
	/** Indicates if the color source is updated or not on the form */
	updated?: boolean;
}

export enum AutomateStateStatusChanges {
	Quote = "AutomateStateStatusChanges:SupplierItem",
	Sample = "AutomateStateStatusChanges:Sample",
}

export enum ColorSourceStatus {
	Active = "Active",
	Inactive = "Inactive",
	Added = "Added",
}

export class ProductSourceStore {
	/** Product Source cnl */
	cnl: cnl;
	/** Product Source Supplier/Factory name */
	name: string;
	/** Color Allocation Options */
	colorAllocationOptions: ColorProductSource[];
	/** GoToDialogHelper for colorway */
	colorwayGoToDialogHelper: GoToDialogHelper;
	/** Color Allocation Values */
	@observable.ref colorAllocationValues: ColorProductSource[];
	/** Color Sources for side panel component */
	@observable colorSources: ColorProductSource[] = [];

	constructor(
		cnl: cnl,
		name: string,
		sourcingType: string,
		colorAllocationOptions: ColorProductSource[],
		colorAllocationValues: ColorProductSource[],
	) {
		makeObservable(this);
		this.cnl = cnl;
		this.name = name;
		this.colorAllocationOptions = colorAllocationOptions;
		this.colorAllocationValues = colorAllocationValues || [];
		this.colorSources = colorAllocationValues;
		this.colorwayGoToDialogHelper = new GoToDialogHelper(
			sourcingType === "StyleSourcing" ? "Colorway" : "ColorMaterial",
			true,
			action((selectedUrl: cnl[]) => {
				this.colorSources = this.colorAllocationOptions.filter((cs) => selectedUrl.includes(cs.id) || cs.disabled);
				this.colorAllocationValues = this.colorSources; 
			}),
		);
	}

	@action onChangeValue = (ev: SyntheticEvent, val: ColorProductSource[]) : void => {
		if (Array.isArray(val)) {
			const colorSourcesMap = new Map(this.colorSources.map((item) => 
				[item.id, item] as [string, ColorProductSource]));
			// to keep the changes on the Allocation Overview side panel - for incative or active action
			this.colorSources = val.map((item) => {
				if (colorSourcesMap.has(item.id)) {
					return colorSourcesMap.get(item.id);
				}
				return item;
			});
			this.colorAllocationValues = val;
		}
	};

	@action onRemoveSelection = (colorwayCnl: cnl) : void => {
		const { colorSources } = this;
		const selectedColorSources = colorSources.filter((cps) => cps.id !== colorwayCnl);
		this.colorSources = selectedColorSources;
		this.colorAllocationValues = this.colorAllocationValues.filter((item) => item.id !== colorwayCnl);
	};

	@action onInactiveColorSource = (colorwayCnl: cnl) : void => {
		const { colorSources } = this;
		const colorSource = colorSources.find((cs) => cs.id === colorwayCnl);
		colorSource.active = false;
		colorSource.status = ColorSourceStatus.Inactive;
		colorSource.updated = !colorSource.updated;
	};

	@action onActiveColorSource = (colorwayCnl: cnl) : void => {
		const { colorSources } = this;
		const colorSource = colorSources.find((cs) => cs.id === colorwayCnl);
		colorSource.active = true;
		colorSource.status = ColorSourceStatus.Active;
		colorSource.updated = !colorSource.updated;
	};

	/**
	 * Return true if the product source has color sources updated.
	 */
	@computed get hasColorAllocationChanged(): boolean {
		const { colorSources } = this;
		return colorSources.some((cs) => cs.updated);
	}

	/**
	 * Return true if the product source has color sources added.
	 */
	@computed get hasColorSourcesAdded(): boolean {
		const { colorSources } = this;
		return colorSources.some((cs) => cs.status === ColorSourceStatus.Added);
	}

	/** goToDialog onClick that opens with a node list */
	@action onClickProductSourceGoToDialog = (e: SyntheticEvent): void => {
		const { colorwayGoToDialogHelper, colorAllocationOptions, colorSources } = this;
		const disabledIds = new Set<string>(colorSources.filter((item) => item.disabled).map((item) => item.id));
		const resultUrls = colorAllocationOptions.map((item) => item.id);
		const currentSelections = colorSources.map((item) => item.node);
		colorwayGoToDialogHelper.openGoToDialogWithCNLs(resultUrls, currentSelections, disabledIds);
	};
}

export class ColorwayAllocationManageStore extends WizardStore {
	readonly #cache: ICache = new WorkingCache();

	readonly titleRes: string = "EnableColorwayAllocation_Title";

	readonly productSourceStores: ProductSourceStore[];

	readonly #sourcingNode: C8Node;

	// Map of notification messages per product type (Style/Material)
	readonly notificationMsgMap = {
		Style: {
			Quotes: "ColorwayAllocationInactivatedMessage_Quotes",
			Samples: "ColorwayAllocationInactivatedMessage_Samples",
			Both: "ColorwayAllocationInactivatedMessage_Both",
			AllocationMainInfo: "ColorwayAllocationEnableMessage",
			AllocationEmptyMainInfo: "ColorwayAllocationEmptyProductSourcesMessage",
		},
		Material: {
			Quotes: "ColorMaterialAllocationInactivatedMessage_Quotes",
			Samples: "ColorMaterialAllocationInactivatedMessage_Samples",
			Both: "ColorMaterialAllocationInactivatedMessage_Both",
			AllocationMainInfo: "ColorMaterialAllocationEnableMessage",
			AllocationEmptyMainInfo: "ColorMaterialAllocationEmptyProductSourcesMessage",
		},
	};

	/** FormData for the wizard */
	formData: CsiFormData;

	/** Node where the action is called from */
	actionSourceNode: C8Node;

	/** variable to store message according to the color allocation configuration */
	allocatedMessageRes: string;

	constructor(
		cache: ICache,
		locale: ILocaleProvider,
		onClose: () => void,
		onSave: ControllerSaveFunction,
		productSourceStores: ProductSourceStore[],
		sourcingNode: C8Node,
		formData: CsiFormData,
		actionSourceNode: C8Node,
	) {
		const stepResourceId = sourcingNode.$Type === "StyleSourcing" ? "ManageColorwayAllocation" : "ManageColorMaterialAllocation";
		super(2, stepResourceId, onClose, onSave, locale);
		makeObservable(this);
		this.#cache = cache;
		this.productSourceStores = productSourceStores;
		this.#sourcingNode = sourcingNode;
		this.formData = formData;
		this.actionSourceNode = actionSourceNode;
		this.allocatedMessageRes = this.#resolveAllocationMessage();
	}

	/**
	 * @override
	 * Disable the finish button if the user is on the first page and no color sources are selected.
	 */
	protected _disableFinishButton = (): boolean => {
		if (this.index === 0) {
			return !this.hasColorAllocationChanged;
		}
		return false;
	};

	/**
	 * @override
	 * Disable the next button if the user is on the first page and no color sources are selected.
	 */
	protected _disablePrimaryButton = (): boolean => {
		if (this.index === 0) {
			return !this.hasColorAllocationAdded;
		}
		return false;
	};

	/**
	 * @override
	 * Display in all pages.
	 */
	_showFinishButton = (): boolean => {
		return true;
	};

	/**
    * Resolve string for wizard message according to the allocation
    */
	#resolveAllocationMessage = (): string => {
		if (this.product.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP") {
			return "";
		}
		if (this.productSourceStores.length > 0 && this.#hasProductColorwayOptionsAvailable()) {
			return this.notificationMsgMap[this.product.$Type].AllocationMainInfo;
		} else {
			return this.notificationMsgMap[this.product.$Type].AllocationEmptyMainInfo;
		}
	};

	/**
    * Return string for inactivated message according to style config selection
    */
	@computed get inactivatedMessageText(): string {
		if (this.hasColorAllocationInactivated) {
			const { product, notificationMsgMap } = this;
			const productType = product.$Type;
			if (this.hasAutomatedQuoteChanges && this.hasAutomatedSampleChanges) {
				return this.locale.resource(notificationMsgMap[productType].Boths, [this.sampleChangeToStatusValue]);
			} else if (this.hasAutomatedQuoteChanges) {
				return this.locale.resource(notificationMsgMap[productType].Quotes);
			} else if (this.hasAutomatedSampleChanges) {
				return this.locale.resource(notificationMsgMap[productType].Samples, [this.sampleChangeToStatusValue]);
			}
		}
		return "";
	};

	/**
	 * Promise to send the request to SetColorAllocation to server
	 */
	protected onSavePromise = async (): Promise<JsonResponse> => {
		const newColorAllocationMap: Record<cnl, string> = {};
		const activeColorAllocationMap: Record<cnl, string> = {};
		const inactiveColorAllocationMap: Record<cnl, string> = {};

		this.productSourceStores
			.filter((ps) => ps.colorSources.length > 0)
			.forEach((item) => {
				newColorAllocationMap[item.cnl] = this.#getUpdatedColorProductsAsStringByStatus(item.colorSources, ColorSourceStatus.Added);
				activeColorAllocationMap[item.cnl] = this.#getUpdatedColorProductsAsStringByStatus(item.colorSources, ColorSourceStatus.Active); 
				inactiveColorAllocationMap[item.cnl] = this.#getUpdatedColorProductsAsStringByStatus(item.colorSources, ColorSourceStatus.Inactive); 
			});
		const supplierRequestUrls = this.tableSelectedNodes.map((node) => node.$URL);
			
		try {
			const res = await setColorAllocation(this.#sourcingNode.$URL, supplierRequestUrls, newColorAllocationMap, activeColorAllocationMap, inactiveColorAllocationMap);
			return res;
		} catch (err) {
			message.errorDialog({ message: err.message });
			return null;
		}
	};

	/**
	 * Return a string of colorway or color product color(for new Added) cnl separated by comma.
	 */
	#getUpdatedColorProductsAsStringByStatus(colorSources: ColorProductSource[], status: string): string {
		return colorSources
			.filter((cs) => cs.status === status && cs.updated)
			.map((cs) => status === ColorSourceStatus.Added ? cs.id: cs.cnl)
			.join(",");
	}

	/**
	 * Return true if any of the product source has color sources selected.
	 */
	@computed get areColorSourcesSelected(): boolean {
		const { productSourceStores } = this;
		return productSourceStores.some((item) => item.colorSources.length > 0);
	}

	/**
	 * Return true if there are colorways available to be allocated
	 */	
	#hasProductColorwayOptionsAvailable(): boolean {
		const { productSourceStores } = this;
		return productSourceStores.some((item) => item.colorAllocationOptions.some((option) => xml.isValidURL(option.id)));
	}

	/**
	 * Return true if there are Color sources added.
	 */
	@computed get hasColorAllocationAdded(): boolean {
		const { productSourceStores } = this;
		return productSourceStores.some((item) => item.hasColorSourcesAdded);
	}

	/**
	 * Return true if any of the product source has color sources updated.
	 */
	@computed get hasColorAllocationChanged(): boolean {
		const { productSourceStores } = this;
		return productSourceStores.some((item) => item.hasColorAllocationChanged);
	}

	/**
	 * Return true if any of the product sources has a color source updated and a color source changed to inactive.
	 */
	@computed get hasColorAllocationInactivated(): boolean {
		const { productSourceStores } = this;
		return productSourceStores.some((item) => item.hasColorAllocationChanged &&
												  this.#getUpdatedColorProductsAsStringByStatus(item.colorSources, ColorSourceStatus.Inactive).length > 0);
	}

	/**
	 * Return true if Quote is selected in AutomateStateStatusChanges config
	 */
	@computed get hasAutomatedQuoteChanges(): boolean {
		return ProductType.get(this.product, "AutomateStateStatusChanges") &&
			ProductType.get(this.product, "AutomateStateStatusChanges")
				.some((item: string) => item === AutomateStateStatusChanges.Quote) &&
			ProductType.get(this.product, "QuoteChangeFromState").length > 0;
	}

	/**
	 * Return true if Sample is selected in AutomateStateStatusChanges config and SampleChangeFromStatus and SampleChangeToStatus are selected
	 */
	@computed get hasAutomatedSampleChanges(): boolean {
		return ProductType.get(this.product, "AutomateStateStatusChanges") &&
			ProductType.get(this.product, "AutomateStateStatusChanges")
				.some((item: string) => item === AutomateStateStatusChanges.Sample) &&
			ProductType.get(this.product, "SampleChangeFromStatus").length > 0 &&
			this.sampleChangeToStatusValue !== "";
	}

	/**
	 * Return the SampleChangeToStatus enum value
	 */
	@computed get sampleChangeToStatusValue(): string {
		return xml.enumGetText(ProductType.get(this.product, "SampleChangeToStatus"));
	}

	/**
	 * Get a list of sources for products that have had colors added.
	 */
	@computed get productSourceStoresWithColorAdded(): ProductSourceStore[] {
		const { productSourceStores } = this;
		return productSourceStores.filter((item) => item.hasColorSourcesAdded);
	}

	get product(): C8Node {
		return this.#cache.get(this.#sourcingNode.__Parent__);
	}

	/** Get formData selected Nodes */
	get tableSelectedNodes(): C8Node[] {
		return this.formData.getSelectedNodes();
	}

	/**Get table view definition to show on 2nd page of wizard and return Table View params */
	get viewParams(): Object {
		const rc = reflection.getClass(this.actionSourceNode.$Type);
		const defView = def.getClassViewDef(rc, "UpdateSupplierRequestPerActiveColor") as View;
		const psNodes = this.#cache.getNodes(this.productSourceStoresWithColorAdded.map((ps) => ps.cnl));
		defView.classControllerParams = { productSources: psNodes };
		defView.setFormData(this.formData);

		return {
			bPopup: true,
			defView: defView,
		};
	}
}

export const ColorwayAllocationManageStoreContext = createContext<ColorwayAllocationManageStore>(undefined);
export const useColorwayAllocationManageStore = (): ColorwayAllocationManageStore => useContext(ColorwayAllocationManageStoreContext);